import fetchPolyfill from 'whatwg-fetch';
import { isArray } from 'lodash';

export default class M2Client {
    static #cachePrefix = 'm_cache_';

    #restEndpoint;
    #socketEndpoint;

    #callsInFlight = {};

    static #restUrlParamMatch = /({\w+})/g;

    /**
     * Use `::instance(restEndpoint, socketEndpoint)` as this will reuse an instance if the endpoints are the same
     *
     * @param {String} restEndpoint
     * @param {String} socketEndpoint
     * @returns {M2Client}
     */
    constructor(restEndpoint, socketEndpoint) {
        this.#restEndpoint = restEndpoint;
        this.#socketEndpoint = socketEndpoint;
    }

    /**
     *
     */
    static #instances = {};

    /**
     *
     * @param {String} restEndpoint
     * @param {String} socketEndpoint
     * @returns {M2Client}
     */
    static instance(restEndpoint, socketEndpoint) {
        const instanceIdx = `${restEndpoint} --- ${socketEndpoint}`;

        if (this.#instances[instanceIdx]) {
            return this.#instances[instanceIdx];
        }

        return this.#instances[instanceIdx] = new this(restEndpoint, socketEndpoint);
    }

    /**
     * *Format*: {
     *  hook: '<String>',
     *  fn: <Function()>,
     *  priority: 0
     * }
     *
     *
     */
    static #hooks = [];

    /**
     *
     * @param {String} hook Name of the hook to be used
     * @param {*} fn
     * @param {*} priority
     */
    static addHook(hook, fn, priority) {
        priority = Number.isInteger ? priority : 0;
        this.#hooks.push({
            hook,
            fn,
            priority,
        });
    }

    static removeHook(hook, fn, priority) {
        priority = Number.isInteger ? priority : 0;
        this.#hooks = this.#hooks.filter(addon => addon.hook !== hook && addon.fn !== fn, addon.priority !== priority);
    }

    static async callHooks(hook, ...args) {
        return this.#hooks
            .filter(addon => addon.hook === hook)
            .sort((a, b) => a.priority > b.priority ? 1 : b.priority > a.priority ? -1 : 0)
            .reduce(async (carry, addon) => {
                return carry.then(() => {
                    if (addon.fn instanceof AbstractHook) {
                        return (addon.fn.isNeeded(...args)) ? addon.fn.invoke(...args) : Promise.resolve();
                    }

                    return addon.fn.apply(null, args);
                });
            }, Promise.resolve());
    }

    /**
     *
     * @param {Abstra} addon
     */
    static attachAddon(addon) {
        addon.attachHooks(this);
    }


    /**
     *
     * @param {Object} callConfig Call config is an object with the following properties
     * @param {String} callConfig.callType (Rest|WebSocket)
     * Rest CallType
     * @param {String} callConfig.url Rest Only: Url for rest call
     * @param {Object} callConfig.urlParams Rest Only: URL params used in the URL, any not set inside the url via {} will be appended as query strings (note for cache purposes avoid query strings where possible)
     * @param {String} callConfig.restMethod Rest Only: The call Method type | Get Post Put Delete
     * WebSocket CallType
     * @param {String} callConfig.socketName WebSocket Only: Socket name
     *
     * @param {String} callConfig.localCacheType Public, Private, Destructive, Null/False (no cache)
     * @param {Number} callConfig.cacheTTL In seconds how long to cache private data locally (only for private call data, public data uses CDN TTL)
     * @param {Boolean} callConfig.cacheStaleWhileRevalidate If set to true will serve stale content from local storage while revalidating
     * @param {Object} callConfig.data Data object to pass in as the Rest body or the WebSocket data
     * @returns
     */
    async makeCall(callConfig) {
        // Validate Call Config
        const callValidationError = this.#validateCallConfig(callConfig);
        if (callValidationError){
            return {status: 'error', data: callValidationError};
        }

        // Handle Call in flight
        if (this.#checkIfCallInFlight(callConfig)) {
            // Call in flight, return exsisting call
            return this.#getInFlightCall(callConfig);
        } else {
            // Call not in flight, add it to the list
            this.#addCallToInFlightList(callConfig);
        }

        let callData;
        // Handle Cache
        let cacheResponse = await this.#getCallCache(callConfig);
        if (cacheResponse && !this.#callRevalidating(callConfig)) {
            // Set call data to cache response
            callData = cacheResponse;
        } else if (cacheResponse && this.#callRevalidating(callConfig)) {
            // Set call data to cache response, but trigger call revalidate
            callData = cacheResponse;
            setTimeout(async () => {
                let callrevalidateData = await this.#handleCall(callConfig);
                // Resolve In Call Flight
                this.#resolveCallInFlightList(callConfig, callrevalidateData);

                // Set Cache data if call is cacheable
                this.#setCallCache(callConfig, callrevalidateData);
            }, 1);
            callData = cacheResponse;
        } else {
            callData = await this.#handleCall(callConfig);
        }

        // Resolve In Call Flight
        this.#resolveCallInFlightList(callConfig, callData);

        // Set Cache data if call is cacheable
        if (!this.#callRevalidating(callConfig)) {
            this.#setCallCache(callConfig, callData);
        }
        return callData;
    }

    #setCallCache(callConfig, callData) {
        if (this.#isCallCacheable(callConfig)) {
            let callConfigHash = this.#getCallConfigHash(callConfig);
            let callCacheString = localStorage.getItem(this.constructor.#cachePrefix + callConfigHash);
            if ((!callCacheString || this.#callRevalidating(callConfig)) && callData.status == 'ok') { // Only set cache if no cache already or cashe is in revalidate mode
                localStorage.setItem(this.constructor.#cachePrefix + callConfigHash, JSON.stringify({
                    expires: Date.now() + (callConfig.cacheTTL * 1000),
                    data: callData
                }));
            }
        } else if (this.#isCallDestructive(callConfig)) {
            // Clear API Cache
            for (let localStorageIndex = 0; localStorageIndex < localStorage.length; localStorageIndex++){
                let localStorageKey = localStorage.key(localStorageIndex);
                if (localStorageKey.startsWith(this.constructor.#cachePrefix)) {
                    localStorage.removeItem(localStorageKey);
                }
            }
        }
    }

    #isCallDestructive(callConfig) {
        return callConfig.localCacheType && callConfig.localCacheType == 'Destructive';
    }

    #resolveCallInFlightList(callConfig, callData) {
        let callConfigHash = this.#getCallConfigHash(callConfig);

        if (
            this.#callsInFlight[callConfigHash] &&
            this.#callsInFlight[callConfigHash].resolver
        ) {
            this.#callsInFlight[callConfigHash].resolver(callData);
            delete this.#callsInFlight[callConfigHash];
        }
    }

    async #handleCall(callConfig) {
        if (callConfig.callType == 'Rest') {
            return await this.#makeRestCall(callConfig);
        } else if (callConfig.callType == 'WebSocket') {
            return await this.#makeSocketCall(callConfig);
        }
    }

    async #makeSocketCall(callConfig) {
        let callData = {status: 'error', data: 'Sockets Not Implimented'};
        await this.constructor.callHooks('update-socket-request', this, callConfig);
        console.log(callConfig);
        await this.constructor.callHooks('update-socket-response', this, callConfig, callData);
        return callData;
    }

    async #getRequestHeaders() {
        const headers = {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',   // So the proxy can detect that it's an AJAX request
        };

        await this.constructor.callHooks('update-request-headers', this, headers);

        return headers;
    }

    async #makeRestCall(callConfig) {
        let callData = {status: 'error', data: null};

        let request = {
            method: (callConfig.restMethod.toUpperCase()) ? callConfig.restMethod : 'GET',
            mode: 'cors',
            credentials: 'omit',
            headers: await this.#getRequestHeaders(),
            referrerPolicy: 'no-referrer',
        };

        if (request.method !== 'GET' && callConfig.data) {
            request.body = JSON.stringify(callConfig.data);
        }

        let uri = callConfig.url;
        // Set url Params inside URI
        const urlParams = [...callConfig.url.match(this.constructor.#restUrlParamMatch)];
        for (let urlParam of urlParams) {
            let paramName = urlParam.replace('{','').replace('}',''); // Replace { and } to get param property name
            uri = uri.replace(urlParam, callConfig.urlParams[paramName]);
            delete callConfig.urlParams[paramName];
        }

        const url = new URL(this.#restEndpoint + uri);

        if (callConfig.urlParams) {
            Object.keys(callConfig.urlParams).forEach(key => {
                const values = callConfig.urlParams[key];
                if (isArray(values)) {
                    values.forEach(value => {
                        url.searchParams.append(key, value);
                    })
                } else {
                    url.searchParams.append(key, callConfig.urlParams[key]);
                }
            })
        }

        await this.constructor.callHooks('update-rest-request', this, url, request, callConfig);

        let responseData = await fetchPolyfill.fetch(url.toString(), request);

        if (responseData.status == 200) {
            callData.status = 'ok';
        }

        callData.data = await responseData.json();

        await this.constructor.callHooks('update-rest-response', this, url, request, callConfig, callData, responseData);

        return callData;
    }

    #validateCallConfig(callConfig) {
        if (!callConfig) {
            return 'Must provide a valid call config object, null given';
        }

        if (!callConfig.callType || (callConfig.callType != 'Rest' && callConfig.callType != 'WebSocket')) {
            return 'Must provide a valid "callType" property, "Rest" or "WebSocket" accepted';
        }

        if (callConfig.callType == 'Rest') {
            if (!callConfig.url) {
                return 'While in REST mode you must provide a valid "url" property';
            }

            if (callConfig.restMethod && (callConfig.restMethod != 'Get' && callConfig.restMethod != 'Post' && callConfig.restMethod != 'Put' && callConfig.restMethod != 'Delete')) {
                return 'While in REST mode you must provide a valid "restMethod" property';
            }

            // If URL params check if there in the URL Params object
            const urlParams = [...callConfig.url.match(this.constructor.#restUrlParamMatch)];
            for (let urlParam of urlParams) {
                let paramName = urlParam.replace('{','').replace('}',''); // Replace { and } to get param property name
                if (!callConfig.urlParams || !callConfig.urlParams[paramName]) {
                    return `The "urlParams" property object is missing the needed ${paramName} property used in the URL`;
                }
            }
        }

        if (callConfig.callType == 'WebSocket' && !callConfig.socketName) {
            return 'While in WebSocket mode you must provide a valid "socketName" property';
        }

        if (callConfig.cacheTTL && (!callConfig.localCacheType || (callConfig.localCacheType != 'Public' && callConfig.localCacheType != 'Private'))) {
            return 'The "cacheTTL" call config property can only be used when the "localCacheType" property is set to "Public" or "Private"';
        }

        if (callConfig.cacheStaleWhileRevalidate && (!callConfig.localCacheType || (callConfig.localCacheType != 'Public' && callConfig.localCacheType != 'Private'))) {
            return 'The "cacheStaleWhileRevalidate" call config property can only be used when the "localCacheType" property is set to "Public" or "Private"';
        }

        return false;
    }

    #checkIfCallInFlight(callConfig) {
        let callConfigHash = this.#getCallConfigHash(callConfig);
        return callConfigHash in this.#callsInFlight;
    }

    #getCallConfigHash(callConfig) {
        let callName;

        if (callConfig.callType == 'Rest') {
            callName = `${callConfig.restMethod}-${callConfig.url}`;
        } else {
            callName = callConfig.socketName;
        }

        let hash = btoa(JSON.stringify(callConfig));

        return `${callName}__${hash}`;
    }

    #getInFlightCall(callConfig) {
        let callConfigHash = this.#getCallConfigHash(callConfig);
        return this.#callsInFlight[callConfigHash].prommise;
    }

    async #addCallToInFlightList(callConfig) {
        let callConfigHash = this.#getCallConfigHash(callConfig);
        let flightResolver;
        let flightPromise = new Promise(resolve => {
            flightResolver = resolve;
        });

        this.#callsInFlight[callConfigHash] = {
            resolver: flightResolver,
            promise: flightPromise,
        };
    }

    #getCallCache(callConfig) {
        if (this.#isCallCacheable(callConfig)) {
            let callConfigHash = this.#getCallConfigHash(callConfig);
            let callCacheString = localStorage.getItem(this.constructor.#cachePrefix + callConfigHash);

            if (callCacheString && callCacheString != '[object Object]') {
                let cacheData = JSON.parse(callCacheString);

                if (cacheData && cacheData.expires && Date.now() < cacheData.expires) {
                    return cacheData.data;
                } else {
                    // Cache Expired
                    localStorage.removeItem(this.constructor.#cachePrefix + callConfigHash);

                    if (callConfig.cacheStaleWhileRevalidate) {
                        cacheData._revalidate = true;
                        localStorage.setItem(this.constructor.#cachePrefix + callConfigHash, JSON.stringify(cacheData)); // Set Cache with revalidate mode
                        return cacheData.data; // return data and revalidate
                    }

                    return false;
                }
            } else {
                return false; // Call Not in Cache
            }
        } else {
            return false; // Not cachable
        }
    }

    /**
     *
     * @param {*} callConfig
     * @returns Boolean
     */
    #isCallCacheable(callConfig) {
        return callConfig.localCacheType &&
            callConfig.cacheTTL &&
            (
                callConfig.localCacheType == 'Public' ||
                callConfig.localCacheType == 'Private'
            );
    }

    /**
     *
     * @param {*} callConfig
     * @returns {Boolean}
     */
    #callRevalidating(callConfig) {
        let callConfigHash = this.#getCallConfigHash(callConfig);
        let callCacheString = localStorage.getItem(this.constructor.#cachePrefix + callConfigHash);
        return callCacheString && JSON.parse(callCacheString) && JSON.parse(callCacheString)._revalidate;
    }
}

